import axios from "axios";
import { apiKey } from "../../../../config/config";

// ---------------------------------------------------------------------------
// Persistence for Invoice Builder templates.
//
// Proposed backend contract (not implemented on the server yet as of this
// writing — every call below is tried against these routes first):
//
//   GET    /api/rms/admin/invoice-templates            -> [{ id, name, layout, outlet_id, created_at, updated_at }, ...]
//   POST   /api/rms/admin/invoice-templates             body: { name, outlet_id, user_id, layout: <JSON string> }
//   GET    /api/rms/admin/invoice-templates/{id}        -> { id, name, layout, ... }
//   PUT    /api/rms/admin/invoice-templates/{id}        body: { name, layout: <JSON string> }
//   DELETE /api/rms/admin/invoice-templates/{id}
//
// `layout` is a JSON-stringified array of field objects (id, key, type, x, y,
// width, height, style, text, path) — see invoiceFieldPalette.js.
//
// Until those routes exist server-side, every function below falls back to
// browser localStorage so the builder is fully usable today. Once the
// backend adds the routes, these calls will start succeeding automatically
// and the localStorage copy is just a local cache/backup.
// ---------------------------------------------------------------------------

const LOCAL_KEY = "imdady-invoice-templates";

function authHeaders(user) {
  return { headers: { Authorization: `Bearer ${user?.token}` } };
}

function readLocal() {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]");
  } catch {
    return [];
  }
}

function writeLocal(templates) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(templates));
}

export async function listTemplates(user) {
  try {
    const res = await axios.get(
      `${apiKey}/api/rms/admin/invoice-templates`,
      authHeaders(user)
    );
    if (Array.isArray(res.data)) return { data: res.data, source: "server" };
  } catch (err) {
    // fall through to local
  }
  return { data: readLocal(), source: "local" };
}

export async function getTemplate(user, id) {
  const isLocalId = String(id).startsWith("local-");
  if (!isLocalId) {
    try {
      const res = await axios.get(
        `${apiKey}/api/rms/admin/invoice-templates/${id}`,
        authHeaders(user)
      );
      if (res.data) return { data: res.data, source: "server" };
    } catch (err) {
      // fall through to local
    }
  }
  const local = readLocal().find((t) => String(t.id) === String(id));
  return { data: local || null, source: "local" };
}

export async function saveTemplate(user, template) {
  const payload = {
    name: template.name,
    outlet_id: user?.user?.outlet_id,
    user_id: user?.user?.id,
    layout: JSON.stringify(template.fields || []),
  };

  const isNew = !template.id || String(template.id).startsWith("local-");

  try {
    const res = isNew
      ? await axios.post(
          `${apiKey}/api/rms/admin/invoice-templates`,
          payload,
          authHeaders(user)
        )
      : await axios.put(
          `${apiKey}/api/rms/admin/invoice-templates/${template.id}`,
          payload,
          authHeaders(user)
        );
    if (res.data) return { data: res.data, source: "server" };
  } catch (err) {
    // fall through to local
  }

  // Local fallback
  const templates = readLocal();
  const id = isNew ? `local-${Date.now()}` : template.id;
  const record = {
    id,
    name: template.name,
    fields: template.fields || [],
    updated_at: new Date().toISOString(),
  };
  const idx = templates.findIndex((t) => String(t.id) === String(id));
  if (idx >= 0) templates[idx] = record;
  else templates.push(record);
  writeLocal(templates);
  return { data: record, source: "local" };
}

export async function deleteTemplate(user, id) {
  const isLocalId = String(id).startsWith("local-");
  if (!isLocalId) {
    try {
      await axios.delete(
        `${apiKey}/api/rms/admin/invoice-templates/${id}`,
        authHeaders(user)
      );
      return { source: "server" };
    } catch (err) {
      // fall through to local removal too, in case it only exists locally
    }
  }
  writeLocal(readLocal().filter((t) => String(t.id) !== String(id)));
  return { source: "local" };
}
